/**
 * Shared view navigation helpers — Stage 6c (Single-View DOM & Navigation Unification).
 *
 * Provides standardized navigation item configuration, markup generation, and
 * window-reuse helpers for the top navigation bar across index.html, paper.html, and live.html.
 */

export type NavTarget = "home" | "chart" | "paper" | "live";

export interface NavItemConfig {
  id: NavTarget;
  label: string;
  url: string;
  windowName?: string;
}

export const DEFAULT_NAV_ITEMS: readonly NavItemConfig[] = [
  { id: "home", label: "Home", url: "/" },
  { id: "chart", label: "Chart", url: "/chart", windowName: "nc-chart" },
  { id: "paper", label: "Paper", url: "/paper", windowName: "nc-paper" },
  { id: "live", label: "Live", url: "/live", windowName: "nc-live" },
];

export interface BuiltNavItem extends NavItemConfig {
  isActive: boolean;
  title?: string;
  onclick?: string;
}

export function shouldOpenInNewTab(e: {
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  button?: number;
}): boolean {
  return !!(e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1);
}

export function openToolWindow(
  e: {
    ctrlKey?: boolean;
    metaKey?: boolean;
    shiftKey?: boolean;
    button?: number;
    preventDefault?: () => void;
  },
  url: string,
  windowName: string,
  win: Window = window
): boolean {
  if (shouldOpenInNewTab(e)) return true;
  if (e.preventDefault) e.preventDefault();
  const w = win.open(url, windowName, "width=1500,height=940");
  if (!w) {
    win.location.href = url;
    return false;
  }
  try {
    w.focus();
  } catch (_) {}
  return false;
}

export function buildNavItems(
  activeId: NavTarget,
  items: readonly NavItemConfig[] = DEFAULT_NAV_ITEMS
): BuiltNavItem[] {
  return items.map((item) => {
    const isActive = item.id === activeId;
    return {
      ...item,
      isActive,
      title: isActive ? "This window" : undefined,
      onclick: isActive
        ? "return false"
        : item.windowName
          ? `return openTool(event,'${item.url}','${item.windowName}')`
          : undefined,
    };
  });
}

export interface RenderNavOptions {
  items?: readonly NavItemConfig[];
  linkStyle?: string;
  containerClass?: string;
  containerStyle?: string;
}

export function renderNavHtml(
  activeId: NavTarget,
  options: RenderNavOptions = {}
): string {
  const items = buildNavItems(activeId, options.items);
  const containerClass = options.containerClass || "nc-nav";
  const containerStyle =
    options.containerStyle != null
      ? options.containerStyle
      : "display:inline-flex;gap:11px;margin:0 12px 0 4px;font-size:12px;font-weight:600;white-space:nowrap;";
  const defaultLinkStyle = options.linkStyle || "color:#378ADD;text-decoration:none;";

  const links = items
    .map((item) => {
      const classAttr = item.isActive ? ' class="is-active"' : "";
      const titleAttr = item.title ? ` title="${item.title}"` : "";
      const clickAttr = item.onclick ? ` onclick="${item.onclick}"` : "";
      const styleAttr = defaultLinkStyle ? ` style="${defaultLinkStyle}"` : "";
      const ariaCurrent = item.isActive ? ' aria-current="page"' : "";
      return `<a href="${item.url}"${classAttr}${styleAttr}${clickAttr}${titleAttr}${ariaCurrent}>${item.label}</a>`;
    })
    .join("");

  return `<span class="${containerClass}" style="${containerStyle}">${links}</span>`;
}
